'use client'

import cardValidator from 'card-validator'
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useParams } from 'next/navigation'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { useEffectOnce } from 'react-use'
import validator from 'validator'

import { getCSRFToken } from '@/libs/csrf'
import { buildPayPalSdkUrl, formatEGiftPayPalData, PAYPAL_SDK_SCRIPT_ID } from '@/libs/paypal'
import '@/styles/components/_payment_info_Form.scss'

import type { IAllowedPayments } from '../../types/schema'

declare global {
  interface Window {
    paypal?: {
      Buttons: (config: Record<string, unknown>) => { render: (selector: string) => void }
    }
  }
}

type PaymentMethod = 'credit_card' | 'promptpay' | 'alipay' | 'visa' | 'mastercard' | 'jcb' | 'amex' | 'paypal'

type PayPalOrderItem = {
  id: string | number
  quantity: number
}

interface PaymentFormData {
  name: string
  email: string
  phone: string
  countryCode: string
  pMethod: PaymentMethod
  cardNumber?: string
  cardHolder?: string
  expiryMonth?: string
  expiryYear?: string
  cvc?: string
  csrf?: string
  cardToken?: string
  order_type?: string
  promotion?: string
}

interface PaymentInfoFormProps {
  onSubmit: (formData: PaymentFormData) => void
  orderItems: PayPalOrderItem[]
  promotionId: string | number
  allowedPayments?: IAllowedPayments | null
}

const ALL_PAYMENT_METHODS: PaymentMethod[] = ['credit_card', 'promptpay', 'alipay', 'paypal']

function getVisiblePaymentMethods(allowedPayments?: IAllowedPayments | null): PaymentMethod[] {
  if (!allowedPayments || !allowedPayments.methods || allowedPayments.methods.length === 0) {
    return ALL_PAYMENT_METHODS
  }
  if (allowedPayments.status === 1) {
    // include: only show methods in the list
    return ALL_PAYMENT_METHODS.filter((m) => allowedPayments.methods.includes(m))
  } else {
    // exclude: show all except methods in the list
    return ALL_PAYMENT_METHODS.filter((m) => !allowedPayments.methods.includes(m))
  }
}

const PaymentInfoForm: React.FC<PaymentInfoFormProps> = ({ onSubmit, orderItems, promotionId, allowedPayments }) => {
  const t = useTranslations()
  const params = useParams()
  const locale = (params.locale as string) ?? 'en'
  const visiblePaymentMethods = getVisiblePaymentMethods(allowedPayments)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(visiblePaymentMethods[0] || 'credit_card')
  const [formData, setFormData] = useState<PaymentFormData>({
    name: '',
    email: '',
    phone: '',
    countryCode: '66',
    pMethod: visiblePaymentMethods[0] || 'credit_card'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [cardType, setCardType] = useState<string>('')
  const [cardError, setCardError] = useState<string>('')
  const [phoneError, setPhoneError] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')
  const [submitError, setSubmitError] = useState<string>('')
  const [isPayPalProcessing, setIsPayPalProcessing] = useState(false)
  const [isPayPalSDKLoading, setIsPayPalSDKLoading] = useState(false)

  // Refs so PayPal SDK closures always read latest state
  const formDataRef = useRef(formData)
  const orderItemsRef = useRef(orderItems)
  const promotionIdRef = useRef(promotionId)
  const orderRefRef = useRef<number>(0)
  // Initialized with stubs; synced to the real functions after their declaration via useEffect
  const validateEmailRef = useRef<(email: string) => string | null>(() => null)
  const validatePhoneRef = useRef<(localPhone: string, countryCode: string) => string | null>(() => null)

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '')
    const { card } = cardValidator.number(digits)

    setCardType(card?.type || '')

    if (card?.type && !['visa', 'mastercard', 'jcb', 'american-express'].includes(card.type)) {
      setCardError(
        "We aren't accept this credit card type. Please make sure the card type is accepted in our website."
      )
    } else {
      setCardError('')
    }

    if (card?.type && ['visa', 'mastercard', 'jcb', 'american-express'].includes(card.type)) {
      setFormData((prev) => ({
        ...prev,
        pMethod:
          card.type === 'american-express' ? 'amex' : (card.type as 'visa' | 'mastercard' | 'jcb')
      }))
    }

    if (card) {
      if (card.type === 'american-express') {
        return digits
          .replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, (match, g1, g2, g3) =>
            [g1, g2, g3].filter(Boolean).join(' ')
          )
          .slice(0, 17)
      }
      if (card.type === 'diners-club') {
        return digits
          .replace(/^(\d{0,4})(\d{0,6})(\d{0,4}).*/, (match, g1, g2, g3) =>
            [g1, g2, g3].filter(Boolean).join(' ')
          )
          .slice(0, 16)
      }
    }

    return digits
      .replace(/(.{4})/g, '$1 ')
      .trim()
      .slice(0, 19)
  }

  const getCardIcon = (type: string) => {
    switch (type) {
      case 'american-express':
        return (
          <Image
            src='/static/images/payments/amex.png'
            alt='American Express'
            width={32}
            height={20}
            loading='lazy'
            className='payment-form__card-icon'
          />
        )
      case 'visa':
        return (
          <Image
            src='/static/images/payments/visa.png'
            alt='Visa'
            width={32}
            height={20}
            loading='lazy'
            className='payment-form__card-icon'
          />
        )
      case 'mastercard':
        return (
          <Image
            src='/static/images/payments/mastercard.png'
            alt='Mastercard'
            width={32}
            height={20}
            loading='lazy'
            className='payment-form__card-icon'
          />
        )
      case 'jcb':
        return (
          <Image
            src='/static/images/payments/jcb.png'
            alt='JCB'
            width={32}
            height={20}
            loading='lazy'
            className='payment-form__card-icon'
          />
        )

      default:
        return null
    }
  }

  //__INPUT CHANGE HANDLER
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    if (name === 'cardNumber') {
      const formattedValue = formatCardNumber(value)
      setFormData((prev) => ({ ...prev, [name]: formattedValue }))
    } else if (name === 'email') {
      //__CLEAR EMAIL ERROR ON CHANGE
      setEmailError('')
      setFormData((prev) => ({ ...prev, [name]: value }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }
  //__PAYMENT METHOD CHANGE
  const handlePaymentMethodChange = (method: PaymentMethod) => {
    setPaymentMethod(method)
    setFormData((prev) => ({
      ...prev,
      pMethod: method === 'credit_card' ? 'credit_card' : method
    }))
  }

  //__PHONE CHANGE HANDLER - LOCAL NUMBER ONLY
  const handlePhoneChange = (value: string, country: any) => {
    //__CLEAR PHONE ERROR ON CHANGE
    setPhoneError('')

    //__EXTRACT LOCAL NUMBER WITHOUT COUNTRY CODE
    let localNumber = value
    if (country && country.dialCode) {
      // Remove country code from the beginning
      const fullCountryCode = country.dialCode
      if (value.startsWith(fullCountryCode)) {
        localNumber = value.substring(fullCountryCode.length)
      }
    }

    //__AUTO REMOVE LEADING ZERO
    if (localNumber.startsWith('0')) {
      localNumber = localNumber.substring(1)
    }

    //__STORE LOCAL NUMBER AND COUNTRY CODE SEPARATELY
    setFormData((prev) => ({
      ...prev,
      phone: localNumber,
      countryCode: country?.dialCode || '66'
    }))
  }

  //__PHONE VALIDATION HELPER USING GOOGLE-LIBPHONENUMBER
  const validatePhoneNumber = useCallback((localPhone: string, countryCode: string) => {
    const phoneUtil = PhoneNumberUtil.getInstance()

    try {
      //__CONSTRUCT FULL INTERNATIONAL NUMBER
      const fullPhoneNumber = `+${countryCode}${localPhone.replace(/\D/g, '')}`

      //__PARSE THE PHONE NUMBER
      const phoneNumber = phoneUtil.parse(fullPhoneNumber, undefined)

      //__CHECK IF THE NUMBER IS VALID
      if (!phoneUtil.isValidNumber(phoneNumber)) {
        return t('Redeem Gift.ERROR_INPUT_PHONE_FORMAT')
      }

      //__CHECK IF IT'S A POSSIBLE NUMBER (LENGTH AND FORMAT)
      if (!phoneUtil.isPossibleNumber(phoneNumber)) {
        return t('Redeem Gift.ERROR_INPUT_PHONE_FORMAT')
      }

      //__ADDITIONAL CHECKS FOR FAKE PATTERNS
      const phoneDigits = localPhone.replace(/\D/g, '')

      //__PREVENT ALL ZEROS
      if (/^0+$/.test(phoneDigits)) {
        return t('Redeem Gift.ERROR_INPUT_PHONE_FORMAT')
      }

      //__PREVENT OBVIOUSLY FAKE PATTERNS (1111111, 1234567, etc.)
      if (
        /^(.)\1{6,}$/.test(phoneDigits) || // 7+ repeated digits
        /^1234567/.test(phoneDigits) || // Sequential ascending
        /^7654321/.test(phoneDigits) // Sequential descending
      ) {
        return t('Redeem Gift.ERROR_INPUT_PHONE_FORMAT')
      }

      return null
    } catch (error) {
      //__PARSING ERROR MEANS INVALID FORMAT
      return t('Redeem Gift.ERROR_INPUT_PHONE_FORMAT')
    }
  }, [t])

  //__EMAIL VALIDATION HELPER USING VALIDATOR LIBRARY
  const validateEmail = useCallback((email: string) => {
    //__CHECK IF EMAIL IS EMPTY
    if (!email || email.trim() === '') {
      return t('Redeem Gift.ERROR_INPUT_EMAIL_REQUIRED') || 'Email is required'
    }

    //__BASIC EMAIL FORMAT VALIDATION
    if (!validator.isEmail(email)) {
      return t('Redeem Gift.ERROR_INPUT_EMAIL_FORMAT') || 'Please enter a valid email address'
    }

    //__CHECK EMAIL LENGTH (REASONABLE LIMITS)
    if (email.length > 254) {
      return t('Redeem Gift.ERROR_INPUT_EMAIL_FORMAT') || 'Email address is too long'
    }

    //__CHECK FOR COMMON FAKE EMAIL PATTERNS
    const fakePatterns = [
      /test@test\.com/i,
      /fake@fake\.com/i,
      /dummy@dummy\.com/i,
      /example@example\.com/i,
      /@test\.test$/i,
      /@fake\.fake$/i
    ]

    if (fakePatterns.some((pattern) => pattern.test(email))) {
      return t('Redeem Gift.ERROR_INPUT_EMAIL_FORMAT') || 'Please enter a real email address'
    }

    //__CHECK FOR DISPOSABLE EMAIL DOMAINS (BASIC LIST)
    const disposableDomains = [
      '10minutemail.com',
      'tempmail.org',
      'guerrillamail.com',
      'mailinator.com',
      'yopmail.com'
    ]

    const emailDomain = email.split('@')[1]?.toLowerCase()
    if (disposableDomains.includes(emailDomain)) {
      return t('Redeem Gift.ERROR_INPUT_EMAIL_FORMAT') || 'Please use a permanent email address'
    }

    return null
  }, [t])

  // Keep refs in sync with latest state/functions
  useEffect(() => { formDataRef.current = formData }, [formData])
  useEffect(() => { orderItemsRef.current = orderItems }, [orderItems])
  useEffect(() => { promotionIdRef.current = promotionId }, [promotionId])
  useEffect(() => { validateEmailRef.current = validateEmail }, [validateEmail])
  useEffect(() => { validatePhoneRef.current = validatePhoneNumber }, [validatePhoneNumber])

  // ── PayPal Smart Buttons ───────────────────────────────────────────────────
  useEffect(() => {
    if (paymentMethod !== 'paypal') return

    const paypalClientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID
    if (!paypalClientId) {
      setSubmitError('PayPal configuration error. Please contact support.')
      return
    }

    const container = document.getElementById('paypal-button-container-promo')
    if (!container) return
    container.innerHTML = ''

    const initButtons = () => {
      if (!window.paypal) {
        setSubmitError('PayPal failed to load. Please refresh and try again.')
        return
      }

      window.paypal
        .Buttons({
          style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },

          createOrder: async () => {
            const fd = formDataRef.current

            // Trigger native browser required/type validation (shows tooltip bubbles like regular form submit)
            const form = document.querySelector('form.payment-form__container') as HTMLFormElement | null
            if (form && !form.reportValidity()) {
              throw new Error('validation_error')
            }

            // Deeper custom validation (disposable email, fake patterns, libphonenumber)
            const emailErr = validateEmailRef.current(fd.email)
            if (emailErr) {
              setEmailError(emailErr)
              throw new Error('validation_error')
            }
            const phoneErr = validatePhoneRef.current(fd.phone, fd.countryCode)
            if (phoneErr) {
              setPhoneError(phoneErr)
              throw new Error('validation_error')
            }

            if (!fd.csrf) {
              setSubmitError('Session expired. Please refresh the page.')
              throw new Error('missing_csrf')
            }

            setIsPayPalProcessing(true)
            setSubmitError('')

            const phoneUtil = PhoneNumberUtil.getInstance()
            let e164Phone = `+${fd.countryCode}${fd.phone.replace(/\D/g, '')}`
            try {
              const parsed = phoneUtil.parse(e164Phone, undefined)
              e164Phone = phoneUtil.format(parsed, PhoneNumberFormat.E164)
            } catch (_) { }

            const res = await fetch('/payment/paypal/create_order.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: formatEGiftPayPalData({
                language: locale,
                formData: { name: fd.name, email: fd.email, phone: e164Phone },
                orderItems: orderItemsRef.current,
                promotionId: promotionIdRef.current,
                csrf: fd.csrf ?? '',
              }),
            })

            const data = await res.json()
            if (!data.success) {
              setIsPayPalProcessing(false)
              throw new Error(data.error || 'create_order_failed')
            }

            orderRefRef.current = data.orderRef
            return data.id
          },

          onApprove: async (data: { orderID: string }) => {
            const fd = formDataRef.current
            const res = await fetch('/payment/paypal/capture_order.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: new URLSearchParams({
                paypal_order_id: data.orderID,
                orderRef: String(orderRefRef.current),
                csrf: fd.csrf ?? '',
              }),
            })

            const result = await res.json()
            setIsPayPalProcessing(false)

            if (result.success) {
              window.location.href = result.redirect
            } else {
              setSubmitError(result.error || 'Payment could not be completed. Please try again.')
            }
          },

          onError: (err: unknown) => {
            setIsPayPalProcessing(false)
            // Suppress generic message when error came from our own validation
            if (err instanceof Error && err.message === 'validation_error') return
            setSubmitError('PayPal payment failed. Please try again or choose another method.')
          },

          onCancel: () => {
            setIsPayPalProcessing(false)
          },
        })
        .render('#paypal-button-container-promo')
    }

    const existingScript = document.getElementById(PAYPAL_SDK_SCRIPT_ID)
    if (existingScript) {
      initButtons()
    } else {
      setIsPayPalSDKLoading(true)
      const script = document.createElement('script')
      script.id = PAYPAL_SDK_SCRIPT_ID
      script.src = buildPayPalSdkUrl(paypalClientId, { enableFunding: ['paylater'] })
      script.onload = () => { setIsPayPalSDKLoading(false); initButtons() }
      script.onerror = () => { setIsPayPalSDKLoading(false); setSubmitError('Failed to load PayPal. Please try again.') }
      document.head.appendChild(script)
    }
  }, [locale, paymentMethod])
  // ───────────────────────────────────────────────────────────────────

  //__INIT CSRF TOKEN
  useEffectOnce(() => {
    const initializeCSRF = async () => {
      try {
        const csrfToken = await getCSRFToken()
        setFormData((prev) => ({
          ...prev,
          csrf: csrfToken,
          order_type: 'eGift',
          promotion: promotionId.toString()
        }))
      } catch (error) {
        console.error('Failed to get CSRF token:', error)
        setSubmitError('Failed to initialize payment form. Please refresh the page.')
      }
    }

    initializeCSRF()
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // PayPal is handled entirely by the SDK callbacks — skip form submit
    if (paymentMethod === 'paypal') return

    setIsSubmitting(true)
    setSubmitError('')

    //__ENHANCED EMAIL VALIDATION
    const emailValidationError = validateEmail(formData.email)
    if (emailValidationError) {
      setEmailError(emailValidationError)
      setIsSubmitting(false)
      return
    }

    //__ENHANCED PHONE VALIDATION
    const phoneValidationError = validatePhoneNumber(formData.phone, formData.countryCode)
    if (phoneValidationError) {
      setPhoneError(phoneValidationError)
      setIsSubmitting(false)
      return
    }

    let cardToken = undefined

    // Accept all card types for Omise tokenization
    if (
      formData.pMethod === 'credit_card' ||
      formData.pMethod === 'visa' ||
      formData.pMethod === 'mastercard' ||
      formData.pMethod === 'jcb' ||
      formData.pMethod === 'amex'
    ) {
      if (!window.Omise) {
        try {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = 'https://cdn.omise.co/omise.js'
            script.onload = resolve
            script.onerror = reject
            document.body.appendChild(script)
          })
        } catch (error) {
          setSubmitError('Failed to load payment processor. Please try again.')
          setIsSubmitting(false)
          return
        }
      }

      const omisePublicKey =
        process.env.NEXT_PUBLIC_OMISE_PUBLIC_KEY ||
        document.querySelector('meta[name="omise"]')?.getAttribute('content')

      if (!omisePublicKey) {
        setSubmitError('Payment configuration error. Please contact support.')
        setIsSubmitting(false)
        return
      }

      window.Omise.setPublicKey(omisePublicKey)

      if (
        !formData.cardNumber ||
        !formData.cardHolder ||
        !formData.expiryMonth ||
        !formData.expiryYear ||
        !formData.cvc
      ) {
        setSubmitError('Please fill in all credit card fields.')
        setIsSubmitting(false)
        return
      }

      const cardData = {
        name: formData.cardHolder,
        number: formData.cardNumber.replace(/\s/g, ''),
        expiration_month: parseInt(formData.expiryMonth),
        expiration_year: parseInt(formData.expiryYear),
        security_code: formData.cvc
      }

      try {
        cardToken = await new Promise((resolve, reject) => {
          window.Omise.createToken('card', cardData, (statusCode: number, response: any) => {
            if (statusCode === 200) {
              resolve(response.id)
            } else {
              reject(response.message || 'Failed to create payment token')
            }
          })
        })
      } catch (error) {
        setSubmitError(
          typeof error === 'string'
            ? error
            : 'Failed to process payment information. Please check your card details.'
        )
        setIsSubmitting(false)
        return
      }

      if (!cardToken) {
        setSubmitError('Failed to create payment token. Please try again.')
        setIsSubmitting(false)
        return
      }
    }

    //__COMBINE COUNTRY CODE WITH LOCAL NUMBER FOR SUBMISSION USING GOOGLE-LIBPHONENUMBER
    const phoneUtil = PhoneNumberUtil.getInstance()
    let internationalPhoneNumber: string

    try {
      const fullPhoneNumber = `${formData.countryCode}${formData.phone.replace(/\D/g, '')}`
      const phoneNumber = phoneUtil.parse(fullPhoneNumber, undefined)
      internationalPhoneNumber = phoneUtil.format(phoneNumber, PhoneNumberFormat.E164)
    } catch (error) {
      //__FALLBACK TO MANUAL FORMATTING IF PARSING FAILS
      internationalPhoneNumber = `${formData.countryCode}${formData.phone.replace(/\D/g, '')}`
    }

    const finalFormData = {
      ...formData,
      phone: internationalPhoneNumber,
      cardToken: cardToken as string | undefined,
      order_type: 'eGift'
    }

    try {
      onSubmit(finalFormData)
    } catch (error) {
      setSubmitError('Failed to submit form. Please try again.')
    }

    setIsSubmitting(false)
  }

  return (
    <>
      <div className='payment-form__header'>
        {t('Promotion Detail.label_payment_info').toUpperCase()}
      </div>
      <form className='payment-form__container' onSubmit={handleSubmit}>
        <div className='payment-form__row'>
          <div>
            <label className='payment-form__label' htmlFor='name'>
              {t('eGift : Purchase.ONLINEGIFT_your_name')}{' '}
              <span className='payment-form__label-required'>*</span>
            </label>
            <input
              id='name'
              name='name'
              type='text'
              className='payment-form__input'
              placeholder={t('eGift : Purchase.ONLINEGIFT_your_name')}
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <label className='payment-form__label' htmlFor='email'>
              {t('eGift : Purchase.ONLINEGIFT_your_email')}{' '}
              <span className='payment-form__label-required'>*</span>
            </label>
            <input
              id='email'
              name='email'
              type='email'
              className='payment-form__input'
              placeholder={t('Promotion Detail.label_your_email_address')}
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            {emailError && (
              <div className='mt-2 inline-flex items-start rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600'>
                <svg
                  className='mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-red-500'
                  fill='currentColor'
                  viewBox='0 0 20 20'>
                  <path
                    fillRule='evenodd'
                    d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                    clipRule='evenodd'
                  />
                </svg>
                <span>{emailError}</span>
              </div>
            )}
          </div>
        </div>

        <div className='mb-6'>
          <label className='payment-form__label' htmlFor='phone'>
            {t('eGift : Purchase.ONLINEGIFT_your_phone')}{' '}
            <span className='payment-form__label-required'>*</span>
          </label>
          <PhoneInput
            country={'th'}
            value={`${formData.countryCode}${formData.phone}`}
            onChange={handlePhoneChange}
            inputProps={{
              name: 'phone',
              id: 'phone',
              required: true
            }}
            containerClass='phone-input-container'
            inputClass='payment-form__input'
            buttonClass=''
            dropdownClass=''
            countryCodeEditable={false}
            disableCountryCode={false}
          />
          {phoneError && (
            <div className='mt-2 inline-flex items-start rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600'>
              <svg
                className='mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-red-500'
                fill='currentColor'
                viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                  clipRule='evenodd'
                />
              </svg>
              <span>{phoneError}</span>
            </div>
          )}
        </div>

        {/* Payment Method Tabs */}
        <div className='payment-form__tabs'>
          {visiblePaymentMethods.includes('credit_card') && (
            <button
              type='button'
              data-track='creditcard'
              className={`payment-form__button--tab ${paymentMethod === 'credit_card' ? 'active' : 'inactive'}`}
              onClick={() => handlePaymentMethodChange('credit_card')}
            >
              <Image src='/static/images/olb/creditcard.png' alt='' width={35} height={35} className='payment-form__method-icon' aria-hidden='true' />
              {t('eGift : Purchase.ONLINEGIFT_credit_card')}
            </button>
          )}
          {visiblePaymentMethods.includes('promptpay') && (
            <button
              type='button'
              data-track='promptpay'
              className={`payment-form__button--tab ${paymentMethod === 'promptpay' ? 'active' : 'inactive'}`}
              onClick={() => handlePaymentMethodChange('promptpay')}
            >
              <Image src='/static/images/olb/promptpay01.png' alt='' width={35} height={35} className='payment-form__method-icon' aria-hidden='true' />
              {t('Menu.PROMPTPAY_HEADER')}
            </button>
          )}
          {visiblePaymentMethods.includes('alipay') && (
            <button
              type='button'
              data-track='alipay'
              className={`payment-form__button--tab ${paymentMethod === 'alipay' ? 'active' : 'inactive'}`}
              onClick={() => handlePaymentMethodChange('alipay')}
            >
              <Image src='/static/images/olb/alipay01.png' alt='' width={35} height={35} className='payment-form__method-icon' aria-hidden='true' />
              {t('eGift : Purchase.ALIPAY_HEADER')}
            </button>
          )}
          {visiblePaymentMethods.includes('paypal') && (
            <button
              type='button'
              data-track='paypal'
              className={`payment-form__button--tab ${paymentMethod === 'paypal' ? 'active' : 'inactive'}`}
              onClick={() => handlePaymentMethodChange('paypal')}
            >
              <Image src='/static/images/olb/paypal.png' alt='' width={35} height={35} className='payment-form__method-icon' aria-hidden='true' />
              PayPal
            </button>
          )}
        </div>

        <div className='payment-form__payment-content'>

          {/* Credit Card Fields */}
          {paymentMethod === 'credit_card' && (
            <>
              <div className='payment-form__row'>
                <div>
                  <label className='payment-form__label' htmlFor='cardNumber'>
                    {t('Menu.CREDIT_CARD_NUMBER')}
                    <span className='payment-form__label-required'>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id='cardNumber'
                      name='cardNumber'
                      type='text'
                      className='payment-form__input'
                      placeholder={t('Online Booking.CARDNAME_PLACEHOLDER')}
                      value={formData.cardNumber || ''}
                      onChange={handleInputChange}
                      required
                      maxLength={19}
                      style={{ paddingRight: '45px' }}
                    />
                    {cardType && (
                      <div
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}>
                        {getCardIcon(cardType)}
                      </div>
                    )}
                  </div>
                  {cardError && (
                    <div style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                      {cardError}
                    </div>
                  )}
                </div>
                <div>
                  <label className='payment-form__label' htmlFor='cardHolder'>
                    {t('Menu.CREDIT_CARD_NAME')}
                    <span className='payment-form__label-required'>*</span>
                  </label>
                  <input
                    id='cardHolder'
                    name='cardHolder'
                    type='text'
                    className='payment-form__input'
                    placeholder={t('Menu.CREDIT_CARD_NAME')}
                    value={formData.cardHolder || ''}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className='payment-form__row-three'>
                <div>
                  <label className='payment-form__label' htmlFor='expiryMonth'>
                    {t('Menu.CREDIT_CARD_EXPIRY')}{' '}
                    <span className='payment-form__label-required'>*</span>
                  </label>
                  <div className='payment-form__select-container'>
                    <select
                      id='expiryMonth'
                      name='expiryMonth'
                      className='payment-form__select'
                      value={formData.expiryMonth || ''}
                      onChange={handleInputChange}
                      required>
                      <option value=''>MM</option>
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <select
                      id='expiryYear'
                      name='expiryYear'
                      className='payment-form__select'
                      value={formData.expiryYear || ''}
                      onChange={handleInputChange}
                      required>
                      <option value=''>YYYY</option>
                      {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() + i).map(
                        (year) => (
                          <option key={year} value={year.toString()}>
                            {year}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>
                <div>
                  <label className='payment-form__label' htmlFor='cvc'>
                    {t('Menu.CREDIT_CARD_SECURE')}{' '}
                    <span className='payment-form__label-required'>*</span>
                  </label>
                  <input
                    id='cvc'
                    name='cvc'
                    type='password'
                    className='payment-form__input'
                    placeholder={t('Menu.CREDIT_CARD_SECURE')}
                    value={formData.cvc || ''}
                    onChange={handleInputChange}
                    required
                    pattern='\d{3,4}'
                    maxLength={4}
                    inputMode='numeric'
                  />
                </div>
              </div>
            </>
          )}

        </div>{/* end payment-form__payment-content */}

        <div className='payment-form__button-container'>
          {submitError && (
            <div
              style={{ color: 'red', fontSize: '14px', marginBottom: '10px', textAlign: 'center' }}>
              {submitError}
            </div>
          )}
          {isPayPalProcessing && (
            <div className='payment-form__paypal-processing'>
              <span>{t('Menu.PAYMENT_PROCESSING') || 'Processing payment…'}</span>
            </div>
          )}
          {isPayPalSDKLoading && (
            <div className='payment-form__paypal-processing'>
              <span>{t('Menu.PAYPAL_LOADING') || 'Loading PayPal…'}</span>
            </div>
          )}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button type='submit' className='payment-form__button--primary' disabled={isSubmitting || isPayPalProcessing}>
              {isSubmitting ? (t('Menu.PAYMENT_PROCESSING') || 'PROCESSING...') : (t('Online Booking.CONFIRM_BUTTON') || 'CONFIRM')}
            </button>
            {paymentMethod === 'paypal' && (
              <div id='paypal-button-container-promo' className='payment-form__paypal-overlay' />
            )}
          </div>
        </div>
      </form>
    </>
  )
}

export default PaymentInfoForm
